<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { useEligibilityStore, type EligibilitySearchForm } from '@/stores/useEligibilityStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import CompanySearchOdooInput from '@/components/features/eligibility/CompanySearchOdooInput.vue'
import EligibilityDetailsModal from '@/components/features/eligibility/EligibilityDetailsModal.vue'
import EligibilityHistoryTable from '@/components/features/eligibility/EligibilityHistoryTable.vue'

const eligibilityStore = useEligibilityStore()
const boostCoreStore = useBoostCorePermissionStore()
const $toast = useToast()

const { searchResults, isSearching } = storeToRefs(eligibilityStore)
const isAdv = computed(() => boostCoreStore.isAdv)

const searchForm = ref<EligibilitySearchForm>({
  companyUuid: undefined,
  fullTextAddress: '',
  codeBatiment: '',
})

// Modal state
const isModalOpen = ref(false)
const isModalSearchOpen = ref(false)
const selectedEligibility = ref<any>(null)

onMounted(async () => {
  await eligibilityStore.fetchEligibilityHistory()
})

const hasCompanyRef = computed(() => !!searchForm.value.companyUuid)

async function checkEligibility() {
  if (!hasCompanyRef.value && (!searchForm.value.fullTextAddress || !searchForm.value.codeBatiment)) {
    return
  }
  eligibilityStore.resetSearchResults()
  isModalSearchOpen.value = true
  try {
    const sanitizedForm: EligibilitySearchForm = {
      companyUuid: searchForm.value.companyUuid,
      fullTextAddress: String(searchForm.value.fullTextAddress || ''),
      codeBatiment: String(searchForm.value.codeBatiment || ''),
    }

    const data = await eligibilityStore.checkEligibility(sanitizedForm)
    if (data) {
      await eligibilityStore.fetchEligibilityHistory()
    } else {
      isModalSearchOpen.value = false
    }
  } catch (error) {
    const msg = error instanceof Error ? error.message : "Erreur lors de la vérification d'éligibilité"
    $toast.error(msg)
    isModalSearchOpen.value = false
  }
}

async function viewEligibilityDetails(eligibilityId: string) {
  try {
    isModalOpen.value = true
    selectedEligibility.value = null // Afficher le loading
    const eligibility = await eligibilityStore.fetchEligibilityDetails(eligibilityId)
    if (eligibility) {
      selectedEligibility.value = eligibility
    }
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Erreur lors de la récupération des détails'
    $toast.error(msg)
    closeModal()
  }
}

function closeModal() {
  isModalOpen.value = false
  selectedEligibility.value = null
}
function closeModalSearch() {
  isModalSearchOpen.value = false
  searchResults.value = null
}

function onCompanySelect(company: { uuid: string; name: string; metadata?: { street?: string; street2?: string; zip?: string; city?: string } }) {
  if (company.metadata) {
    const parts = [company.metadata.street, company.metadata.street2, company.metadata.zip, company.metadata.city].filter(Boolean)
    searchForm.value.fullTextAddress = parts.join(', ')
  }
}

async function exportEligibility(uuid?: string) {
  if (!uuid) {
    $toast.error("Aucune éligibilité à exporter")
    return
  }
  try {
    await eligibilityStore.exportToCsv(uuid, {
      commitmentMonths: 36,
      technology: 'FTTH',
      bandwidthMbps: 1000,
    })
  } catch (error) {
    $toast.error(error instanceof Error ? error.message : "Erreur lors de l'export")
  }
}

</script>

<template>
  <div class="p-6">
    <Bs2Title title="Éligibilités All-In-One" subtitle="Vérifiez l'éligibilité d'une adresse et consultez votre historique" />

    <!-- Section 1: Formulaire de recherche d'éligibilité -->
    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <h2 class="card-title mb-4">Nouvelle recherche d'éligibilité</h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <CompanySearchOdooInput
              v-model="searchForm.companyUuid"
              label="Raison sociale ou SIREN / SIRET"
              placeholder="Ex: Nom entreprise ou 123456789 ou 12345678900001"
              @select="onCompanySelect"
            />
          </div>
          <div />
          <TextInput v-model="searchForm.fullTextAddress" label="Adresse" placeholder="Entrez l'adresse complète (auto-rempli si société sélectionnée)" />
          <TextInput v-model="searchForm.codeBatiment" label="Code immeuble" placeholder="Entrez le code immeuble (optionnel)" />
          <a href="https://cartefibre.arcep.fr/" target="_blank" rel="noopener noreferrer" class="btn btn-xs btn-primary gap-1 w-fit">
            Trouver mon code immeuble sur ARCEP
          </a>
        </div>

        <div class="flex items-center mt-4 gap-4">
          <ButtonDaisy
            text="Vérifier l'éligibilité"
            variant="primary"
            :icon="MagnifyingGlassIcon"
            :disabled="(!hasCompanyRef && (!searchForm.codeBatiment || !searchForm.fullTextAddress)) || isSearching"
            @click="checkEligibility"
          />
        </div>
      </div>
    </div>

    <!-- Section 3: Historique des éligibilités -->
    <div class="mt-8">
      <EligibilityHistoryTable :is-adv="isAdv" :on-view-details="viewEligibilityDetails" />
    </div>

    <!-- Modal pour les détails -->
    <EligibilityDetailsModal
      :is-open="isModalSearchOpen"
      :eligibility-data="searchResults"
      :loading-message="isSearching ? 'L\'éligibilité est en cours de vérification. Cela peut prendre quelques secondes, veuillez patienter. Les résultats s\'afficheront bientôt.' : undefined"
      @close="closeModalSearch"
      @export="exportEligibility(searchResults?.uuid)"
    />
    <EligibilityDetailsModal :is-open="isModalOpen" :eligibility-data="selectedEligibility" @close="closeModal" @export="exportEligibility(selectedEligibility?.uuid)" />
  </div>
</template>
